import { Button, ButtonRole, ButtonTargetKind } from "@components/Button";
import { ErrorMessage } from "@components/ErrorMessage";
import { Icon, IconSize, IconDisplay } from "@components/Icon";
import { Modal } from "@components/Modal";
import styled from "@emotion/styled";
import React, { useContext, useEffect, useState } from "react";

import {
  CANCEL_DAF_PLEDGE_PARAM,
  CANCEL_IRA_PLEDGE_PARAM,
  CANCEL_PLEDGE_TOKEN_PARAM,
  CANCEL_STOCKS_PLEDGE_PARAM,
} from "@every.org/common/src/entity/constants";
import { isUuid } from "@every.org/common/src/helpers/string";
import { cancelDonationRouteSpec } from "@every.org/common/src/routes/donate";

import { AuthContext } from "src/context/AuthContext";
import { AuthStatus } from "src/context/AuthContext/types";
import { useEdoRouter } from "src/hooks/useEdoRouter";
import { horizontalStackCss, spacing } from "src/theme/spacing";
import { queryApi } from "src/utility/apiClient";

const TYPE_TO_PARAM_MAP: Record<"daf" | "ira" | "stocks", string> = {
  daf: CANCEL_DAF_PLEDGE_PARAM,
  ira: CANCEL_IRA_PLEDGE_PARAM,
  stocks: CANCEL_STOCKS_PLEDGE_PARAM,
};

const TYPE_TO_CONTENT_MAP: Record<"daf" | "ira" | "stocks", React.ReactNode> = {
  daf: (
    <React.Fragment>
      <h4>Cancel your pledge?</h4>
      <p>
        Canceling will let us and the nonprofit know that you don&apos;t plan to
        complete this donation.
      </p>
    </React.Fragment>
  ),
  ira: (
    <React.Fragment>
      <h4>Cancel your pledge?</h4>
      <p>
        Canceling will let us and the nonprofit know that you don&apos;t plan to
        complete this donation.
      </p>
    </React.Fragment>
  ),
  stocks: (
    <React.Fragment>
      <h4>Cancel your pledge?</h4>
      <p>
        Canceling will let us and the nonprofit know that you don&apos;t plan to
        complete this donation.
      </p>
      <p>
        If you would like to change the stock symbol or number of shares, please
        cancel this pledge and create a new one with the updated details so we
        can match your transfer and issue the correct receipt.
      </p>
    </React.Fragment>
  ),
};

const StyledContainer = styled.div`
  display: flex;
  gap: ${spacing.m};
  flex-direction: column;
`;

interface CancelPledgeModalProps {
  type: "daf" | "ira" | "stocks";
  donationId?: string | null;
}

export const CancelPledgeModal = ({
  type,
  donationId,
}: CancelPledgeModalProps) => {
  const authStatus = useContext(AuthContext).status;

  const router = useEdoRouter();
  const cancelToken =
    new URLSearchParams(router.search).get(CANCEL_PLEDGE_TOKEN_PARAM) ??
    undefined;

  const [isOpen, setIsOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [canceled, setCanceled] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (donationId) {
      setIsOpen(true);
    }
  }, [donationId]);

  const clearUrl = () => {
    const urlSearchParams = new URLSearchParams(router.search);
    urlSearchParams.delete(TYPE_TO_PARAM_MAP[type]);
    urlSearchParams.delete(CANCEL_PLEDGE_TOKEN_PARAM);

    const newSearch = urlSearchParams.toString();
    const newUrl = `${router.pathname}${newSearch ? `?${newSearch}` : ""}`;
    router.replace(newUrl);
  };

  const onRequestClose = () => {
    setIsOpen(false);
    clearUrl();
  };

  const onCancelPledge = async () => {
    setSubmitting(true);

    const isValidId = isUuid(donationId);

    if (!isValidId) {
      setError("Invalid donation ID");
      setSubmitting(false);
      return;
    }

    try {
      await queryApi(cancelDonationRouteSpec, {
        body: { donationId, cancelToken },
        routeTokens: {},
        queryParams: {},
      });
      setCanceled(true);
      setSubmitting(false);
      setTimeout(() => {
        onRequestClose();
      }, 2000);
      return;
    } catch (e) {
      setError(e.message || "An error occurred while canceling your pledge.");
      setSubmitting(false);
      return;
    }
  };

  if (
    !donationId ||
    !isOpen ||
    (authStatus !== AuthStatus.LOGGED_IN && !cancelToken)
  ) {
    return null;
  }

  return (
    <Modal isOpen={isOpen} onRequestClose={onRequestClose}>
      <StyledContainer data-tname={`cancelPledgeModal__${type}`}>
        {TYPE_TO_CONTENT_MAP[type]}
        {error && <ErrorMessage>{error}</ErrorMessage>}
        <div css={[horizontalStackCss.s, { justifyContent: "flex-end" }]}>
          {!canceled && (
            <Button
              data-tname="keepPledgeButton"
              role={ButtonRole.TEXT_ONLY}
              onClick={{
                kind: ButtonTargetKind.FUNCTION,
                action: onRequestClose,
              }}
            >
              Keep pledge
            </Button>
          )}
          <Button
            submitting={submitting}
            disabled={submitting}
            data-tname={
              canceled ? "closeCancelPledgeModalButton" : "cancelPledgeButton"
            }
            data-bloom-block={canceled ? undefined : true}
            role={canceled ? ButtonRole.SECONDARY : ButtonRole.PRIMARY}
            onClick={{
              kind: ButtonTargetKind.FUNCTION,
              action: canceled ? onRequestClose : onCancelPledge,
            }}
          >
            {canceled ? (
              <span css={[horizontalStackCss.s, { alignItems: "center" }]}>
                <Icon
                  iconImport={() =>
                    import("@components/Icon/icons/CheckMarkIcon")
                  }
                  size={IconSize.SMALL}
                  display={IconDisplay.CURRENT_COLOR}
                />
                <span>Pledge canceled</span>
              </span>
            ) : (
              "Cancel pledge"
            )}
          </Button>
        </div>
      </StyledContainer>
    </Modal>
  );
};
